'use client'

import { useMutation, useQuery } from '@apollo/client'
import { Modal } from '@chain-zen/ui'
import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { INITIATE_VERIFICATION_FOR_WORKSPACE_MEMBER } from '../../data/mutations/member'
import { GET_CONSTRAINT, GET_PUBLIC_WORKSPACE_MEMBERS } from '../../data/queries/workspace'
import { useAuth } from '../../hooks/use-auth'
import type { IWorkspaceMember } from '../../types/user'
import { MembershipAccessType } from '../../types/user'
import type { IWorkspace, TGetConstraintReturn } from '../../types/workspace'
import { tokenUtils } from '../../utils/token-utils'
import { SelectEmailOrPhone } from './select-email-or-phone'
import { VerifyCodeForWorkspaceMember } from './verify-code-for-workspace-member'

// Step type for modal views
type Step = 1 | 2

// State interface for workspace data and selection
interface IWorkspaceState {
  isResending: boolean
  members: IWorkspaceMember[]
  selectedMember: {
    email: string | null
    id: string | null
  }
  step: Step
  workspace: IWorkspace | null
}

// Initial state for workspace data
const initialState: IWorkspaceState = {
  isResending: false,
  members: [],
  selectedMember: {
    email: null,
    id: null
  },
  step: 1,
  workspace: null
}

interface IModalStepProps {
  isResending: boolean
  members: IWorkspaceMember[]
  onClose: () => void
  onResendCode: () => void
  onSelectMember: (id: string) => void
  onVerificationCode: () => void
  selectedMember: IWorkspaceState['selectedMember']
  step: Step
  verifying: boolean
  workspace: IWorkspace | null
}

/**
 * Renders the appropriate step component based on current state
 */
const ModalStep = ({
  step,
  workspace,
  members,
  selectedMember,
  isResending,
  verifying,
  onSelectMember,
  onVerificationCode,
  onClose,
  onResendCode
}: IModalStepProps) => {
  const steps: Record<Step, JSX.Element> = {
    1: (
      <SelectEmailOrPhone
        carrierWorkspaceDisplayName={workspace?.displayName || ''}
        workspaceMembers={members}
        setSelectedMemberIDAction={onSelectMember}
        selectedMemberID={selectedMember.id}
        gettingCode={verifying}
        getVerificationCodeAction={onVerificationCode}
      />
    ),
    2: (
      <VerifyCodeForWorkspaceMember
        constraintWorkspaceID={workspace?.id || ''}
        isResending={isResending}
        selectedEmailOrPhone={selectedMember.email}
        onCloseAction={onClose}
        resendCodeAction={onResendCode}
      />
    )
  }
  return steps[step]
}

/**
 * Modal component for adding a carrier to a workspace
 * Handles member selection and verification process
 * Only works for authenticated users
 */
export const AddCarrierToWorkspace = () => {
  // Local state for workspace data and modal visibility
  const [state, setState] = useState<IWorkspaceState>(initialState)
  const [isOpen, setIsOpen] = useState(false)

  // Check if user is authenticated
  const isAuthenticated = tokenUtils.isAuthenticated()

  // Get workspace name from URL and check if it exists
  const searchParams = useSearchParams()
  const { workspaces = [], isWorkspacesLoading } = useAuth()
  const carrierWorkspacename = searchParams.get('cw')
  const existingWorkspace = workspaces.find((ws) => ws.workspace?.workspacename === carrierWorkspacename)

  // Query to get workspace details if not already in user's workspaces
  const { loading: loadingConstraint } = useQuery<{ getConstraint: TGetConstraintReturn }>(GET_CONSTRAINT, {
    fetchPolicy: 'network-only',
    onCompleted: ({ getConstraint }) => {
      if (getConstraint?.workspace) {
        setState((prev) => ({ ...prev, workspace: getConstraint.workspace }))
      }
    },
    skip: !isAuthenticated || !carrierWorkspacename || isWorkspacesLoading || Boolean(existingWorkspace),
    variables: { constraint: carrierWorkspacename }
  })

  // Query to get active workspace members with email
  const { loading: loadingMembers } = useQuery<{ publicWorkspaceMembers: IWorkspaceMember[] }>(
    GET_PUBLIC_WORKSPACE_MEMBERS,
    {
      context: { headers: { 'ws-id': state.workspace?.id } },
      fetchPolicy: 'network-only',
      onCompleted: ({ publicWorkspaceMembers }) => {
        const activeMembers = publicWorkspaceMembers.filter(
          ({ user: userData, accessType, status }) =>
            status === 'Active' && (userData?.email != null || accessType !== MembershipAccessType.DRIVER)
        )
        if (activeMembers.length > 0) {
          setState((prev) => ({ ...prev, members: activeMembers }))
        }
      },
      skip: !isAuthenticated || isWorkspacesLoading || !state.workspace?.id
    }
  )

  // Mutation to initiate verification process
  const [initiateVerification, { loading: verifying }] = useMutation(INITIATE_VERIFICATION_FOR_WORKSPACE_MEMBER, {
    onCompleted: () => setState((prev) => ({ ...prev, isResending: false, step: 2 })),
    onError: (error) => toast.error(error.message || 'Something went wrong.')
  })

  // Show modal when data is loaded (only for authenticated users who are NOT already in the workspace)
  useEffect(() => {
    if (
      isAuthenticated &&
      !isWorkspacesLoading &&
      !existingWorkspace &&
      !loadingConstraint &&
      !loadingMembers &&
      state.workspace &&
      state.members.length > 0
    ) {
      setIsOpen(true)
    }
  }, [
    isAuthenticated,
    isWorkspacesLoading,
    existingWorkspace,
    loadingConstraint,
    loadingMembers,
    state.workspace,
    state.members
  ])

  // Update selected member's email when ID changes
  useEffect(() => {
    if (!state.selectedMember.id) return
    const member = state.members.find((m) => m.user?.id === state.selectedMember.id)
    const email = member?.user?.email || null
    setState((prev) => ({
      ...prev,
      selectedMember: { ...prev.selectedMember, email }
    }))
  }, [state.selectedMember.id, state.members])

  // Event handlers
  const handleVerificationCode = () => {
    if (!state.workspace?.id || !state.selectedMember.id) return
    initiateVerification({
      variables: {
        workspaceID: state.workspace.id,
        workspaceMemberUserID: state.selectedMember.id
      }
    })
  }

  const handleResendCode = () => {
    handleVerificationCode()
    setState((prev) => ({ ...prev, isResending: true }))
  }

  const handleClose = () => setIsOpen(false)

  const handleSelectMember = (id: string) =>
    setState((prev) => ({ ...prev, selectedMember: { ...prev.selectedMember, id } }))

  return (
    <Modal size="lg" isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={false} showCloseButton={false}>
      <Modal.Body className="p-0">
        <ModalStep
          step={state.step}
          workspace={state.workspace}
          members={state.members}
          selectedMember={state.selectedMember}
          isResending={state.isResending}
          verifying={verifying}
          onSelectMember={handleSelectMember}
          onVerificationCode={handleVerificationCode}
          onClose={handleClose}
          onResendCode={handleResendCode}
        />
      </Modal.Body>
    </Modal>
  )
}
